import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
	SypacButton,
	SypacIcon,
	SypacText,
} from '@sypac/component-library-react';
import '../select-product.scss';
import { OrderContext } from '../../../context/OrderContext/order.context';
import {
	ProductResponse,
	ProductService,
} from '../../../services/product.services';
import { T, useTranslate } from '@tolgee/react';
import ClientStepper from '../../../components/ClientStepper/ClientStepper';
import { GeoService } from '../../../services/geo.services';
import ClientStoreBreadcrumbs from '../../../components/ClientStoreBreadcrumbs/ClientStoreBreadcrumbs';
import { useGetCategories } from '../../../hooks/use-get-categories';
import { CompanyService } from '../../../services/company.services';
import { toastVariant } from '../../../components/ToastVariant/toastVariant';
import {
	CUSTOMER_STORE_FLOW,
	OrderCreationFlow,
} from '../../../types/order-flow';
import useDebounce from '../../../hooks/useDebounce';
import { Geo } from '../../../interfaces/geo.interface';
import { AuthContext } from '../../../context/context';
import SearchLocation from '../ClientSelectLocation/components/SearchLocation';
import Map from '../../../components/Map/Map';
import { MapMarker } from '../../../components/Map/Map.interface';
import { motion } from 'framer-motion';
import { LatLng } from 'leaflet';
import { cn } from '../../../lib/utils';
import DeliveryAddresses from '../../../components/ContractsTable/components/DeliveryAddresses';
import LayeredProgressBar from '../../../components/LayeredProgressBar/LayeredProgressBar';
import { OrderInterface } from '../../../components/OrdersTable/interfaces/Order.interface';
import BankTransfer from '../../../assets/BankTransfer';

const SelectLocationStep: React.FC = () => {
	const { t } = useTranslate();
	const navigate = useNavigate();
	const { order, setOrder, additional } = useContext(OrderContext);
	const { user } = useContext(AuthContext);

	const [search, setSearch] = useState<string>(
		order?.location?.address?.label ?? order?.location?.label ?? '',
	);
	const [locations, setLocations] = useState<Geo[]>([]);
	const [locationsLoading, setLocationsLoading] = useState(false);
	const [hasSearched, setHasSearched] = useState(false);
	const [previewLocation, setPreviewLocation] = useState<Geo | null>(null);
	const [userCountry, setUserCountry] = useState<string>();
	const [products, setProducts] = useState<ProductResponse>({
		items: [],
		count: 0,
	});
	const [isMapFullScreen, setIsMapFullScreen] = useState<boolean>(false);
	const [isLgUp, setIsLgUp] = useState(false);
	const [isMapSelection, setIsMapSelection] = useState<boolean>(false);
	const [mapExactClickPosition, setMapExactClickPosition] =
		useState<LatLng | null>(null);

	const markers: MapMarker[] = [];

	const debouncedSearch = useDebounce(search, 400);
	const flow: OrderCreationFlow =
		additional?.orderFlow ?? CUSTOMER_STORE_FLOW.BASE;
	const isContractOrder = Boolean(order?.contractData);

	const toNum = (v: unknown): number | null => {
		const n =
			typeof v === 'string' ? parseFloat(v) : typeof v === 'number' ? v : NaN;
		return Number.isFinite(n) ? n : null;
	};

	const resolvedProductId = useMemo<number | undefined>(() => {
		const fromOrder = order?.product?.id;
		if (fromOrder) return Number(fromOrder);
		const fromContract =
			order?.contractData?.lines?.[0]?.product?.id ??
			order?.contractData?.lines?.[0]?.productId;
		return fromContract ? Number(fromContract) : undefined;
	}, [order?.product?.id, order?.contractData?.lines]);

	const resolvedCategoryId = useMemo<string | undefined>(() => {
		return (
			order?.category?.id ??
			order?.product?.categoryId ??
			order?.product?.category?.id ??
			order?.contractData?.lines?.[0]?.product?.categoryId ??
			order?.contractData?.lines?.[0]?.product?.category?.id
		);
	}, [order?.category?.id, order?.product, order?.contractData?.lines]);

	const [categories] = useGetCategories({
		ids: resolvedCategoryId ? [String(resolvedCategoryId)] : undefined,
		countryCode: userCountry,
	});
	const currentCategory = useMemo(
		() =>
			categories?.items?.find(
				(i) => String(i.id) === String(resolvedCategoryId),
			),
		[categories?.items, resolvedCategoryId],
	);

	const [groups] = useGetCategories({ countryCode: userCountry });
	const currentGroup = useMemo(() => {
		if (order?.group?.id) return order.group;
		const parentId = currentCategory?.parentId;
		return groups?.items?.find((g) => String(g.id) === String(parentId));
	}, [order?.group, groups?.items, currentCategory]);

	const getMyCompanyCallback = useCallback(async () => {
		try {
			const { data } = await CompanyService.getMeCompany();
			setUserCountry(
				data.countryCode === 'MD'
					? 'MDA'
					: data.countryCode === 'PL'
					? 'POL'
					: '',
			);
		} catch (error: any) {
			toastVariant(`Something went wrong. ${error?.toString()}`, true);
		}
	}, []);

	useEffect(() => {
		getMyCompanyCallback().then(() => {});
	}, [getMyCompanyCallback]);

	const getProducts = useCallback(async () => {
		if (!resolvedCategoryId) return;
		try {
			const resp = await ProductService.getList({
				status: 'all',
				categoryId: String(resolvedCategoryId),
				limit: 100,
				offset: 0,
				role: 'customer',
			});
			setProducts(resp.data);
		} catch {}
	}, [resolvedCategoryId]);

	useEffect(() => {
		getProducts().then(() => {});
	}, [getProducts]);

	const currentProduct = useMemo(() => {
		if (order?.product?.id) return order.product;
		if (resolvedProductId) {
			return products?.items?.find(
				(i) => Number(i.id) === Number(resolvedProductId),
			);
		}
		return order?.contractData?.lines?.[0]?.product;
	}, [
		order?.product,
		products?.items,
		resolvedProductId,
		order?.contractData?.lines,
	]);

	const selectedContractLine = useMemo(() => {
		if (!isContractOrder || !order?.contractData?.lines?.length)
			return undefined;
		if (resolvedProductId == null) return order.contractData.lines[0];
		return order.contractData.lines.find(
			(l: any) => (l?.product?.id ?? l?.productId) === resolvedProductId,
		);
	}, [isContractOrder, order?.contractData?.lines, resolvedProductId]);

	const { orderedQtyTons, deliveredQtyTons } = useMemo(() => {
		const contractOrders = (order?.contractData?.orders ??
			[]) as OrderInterface[];

		if (!contractOrders.length) {
			return {
				orderedQtyTons: Number(selectedContractLine?.orderedQtyTons ?? 0),
				deliveredQtyTons: Number(selectedContractLine?.deliveredQtyTons ?? 0),
			};
		}

		let ordered = 0;
		let delivered = 0;

		for (const o of contractOrders) {
			const trucks = Array.isArray(o?.trucks) ? o.trucks : [];

			for (const truck of trucks) {
				const hasLoadedQuantity =
					truck?.loadedQuantity !== null &&
					truck?.loadedQuantity !== undefined &&
					!Number.isNaN(Number(truck.loadedQuantity));

				if (hasLoadedQuantity) {
					delivered += Number(truck.loadedQuantity) || 0;
				} else {
					ordered += Number(truck?.payload ?? 0) || 0;
				}
			}
		}

		return { orderedQtyTons: ordered, deliveredQtyTons: delivered };
	}, [
		order?.contractData?.orders,
		selectedContractLine?.orderedQtyTons,
		selectedContractLine?.deliveredQtyTons,
	]);

	const allowedOverdraftTons = useMemo(() => {
		const pct = Number(selectedContractLine?.allowedOverdraftPercent);
		const contractQty = Number(selectedContractLine?.contractQtyTons ?? 0);

		if (!pct || pct <= 0 || !contractQty) return undefined;

		return (contractQty * pct) / 100;
	}, [
		selectedContractLine?.allowedOverdraftPercent,
		selectedContractLine?.contractQtyTons,
	]);

	const derivedContractPrices = useMemo(() => {
		if (!selectedContractLine) return undefined;
		const base = Number(selectedContractLine.pricePerTonBuyer ?? 0);
		const markupPct = Number(selectedContractLine.markup ?? 0);
		const vatPct = Number(
			selectedContractLine?.product?.vat ?? currentProduct?.vat ?? 0,
		);
		const pricePerUnit = base;
		const pricePerUnitWithProcent = +(base * (1 + markupPct / 100)).toFixed(2);
		const pricePerUnitWithVat = +(
			pricePerUnitWithProcent *
			(1 + vatPct / 100)
		).toFixed(2);
		return { pricePerUnit, pricePerUnitWithProcent, pricePerUnitWithVat };
	}, [selectedContractLine, currentProduct]);

	const effectiveProduct = useMemo(() => {
		if (selectedContractLine?.product && derivedContractPrices) {
			return {
				...selectedContractLine.product,
				pricePerUnit: derivedContractPrices.pricePerUnit,
				pricePerUnitWithProcent: derivedContractPrices.pricePerUnitWithProcent,
				pricePerUnitWithVat: derivedContractPrices.pricePerUnitWithVat,
			};
		}
		return currentProduct;
	}, [selectedContractLine?.product, derivedContractPrices, currentProduct]);

	useEffect(() => {
		if (!debouncedSearch || isMapSelection) {
			setLocations([]);
			setHasSearched(false);
			return;
		}
		let cancelled = false;
		(async () => {
			setLocationsLoading(true);
			setHasSearched(true);
			try {
				const { data } = await GeoService.getAutosuggest({
					phrase: debouncedSearch,
					countryCode: user?.profile?.countryCode,
					maxResults: '20',
				});
				if (!cancelled) setLocations(data);
			} finally {
				if (!cancelled) setLocationsLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [debouncedSearch, user?.profile?.countryCode, isMapSelection]);

	const handlePreviewLocation = useCallback(
		async (loc: Geo | null) => {
			if (!loc) {
				setPreviewLocation(null);
				return;
			}

			let enriched: Geo & { latitude?: number; longitude?: number } = {
				...loc,
			};

			if ((!enriched.latitude || !enriched.longitude) && enriched.locationId) {
				try {
					const { data } = await GeoService.getLocationDetails({
						locationid: enriched.locationId,
					});
					enriched.latitude = Number(data?.Latitude) || enriched.latitude;
					enriched.longitude = Number(data?.Longitude) || enriched.longitude;
				} catch {}
			}

			if (
				!enriched.countryCode &&
				!enriched.address?.countryCode &&
				enriched.latitude != null &&
				enriched.longitude != null
			) {
				try {
					const { data } = await GeoService.reverseGeocode({
						lat: String(enriched.latitude),
						lng: String(enriched.longitude),
						countryCode: user?.profile?.countryCode,
					});
					const first = data?.items?.[0];
					if (first?.address) {
						const a = first.address;

						enriched = {
							...enriched,
							address: {
								...(enriched.address ?? {}),
								label: enriched.address?.label || a.label || '',
								country: a.countryName || enriched.address?.country || '',
								countryCode:
									a.countryCode || enriched.address?.countryCode || '',
								state: a.state ?? enriched.address?.state,
								county: a.county ?? enriched.address?.county,
								city: a.city ?? enriched.address?.city,
								street:
									a.street ??
									([a.street].filter(Boolean).join(' ') ||
										enriched.address?.street),
								postalCode: a.postalCode ?? enriched.address?.postalCode,
							},
							countryCode: a.countryCode || enriched.countryCode,
						};
					}
				} catch {}
			}

			setPreviewLocation(enriched);
		},
		[user?.profile?.countryCode],
	);

	const handleLocationSelect = useCallback(
		(loc: Geo) => {
			const override =
				isMapSelection && mapExactClickPosition
					? {
							latitude: mapExactClickPosition.lat,
							longitude: mapExactClickPosition.lng,
					  }
					: {};

			(async () => {
				let result: any = { ...loc, ...override };

				const lat =
					toNum(result.latitude) ??
					toNum(result.position?.lat) ??
					toNum(result.position?.Latitude);
				const lng =
					toNum(result.longitude) ??
					toNum(result.position?.lng) ??
					toNum(result.position?.Longitude);

				if (lat != null && lng != null) {
					result.latitude = lat;
					result.longitude = lng;
				}

				if (
					!result.countryCode &&
					!result.address?.countryCode &&
					lat != null &&
					lng != null
				) {
					try {
						const { data } = await GeoService.reverseGeocode({
							lat: String(lat),
							lng: String(lng),
							countryCode: user?.profile?.countryCode,
						});
						const first = data?.items?.[0];
						if (first?.address) {
							const a = first.address;
							result.address = {
								...(result.address ?? {}),
								label: result.address?.label || a.label || '',
								country: a.countryName || result.address?.country || '',
								countryCode: a.countryCode || result.address?.countryCode || '',
								state: a.state ?? result.address?.state,
								county: a.county ?? result.address?.county,
								city: a.city ?? result.address?.city,
								street:
									a.street ??
									([a.street].filter(Boolean).join(' ') ||
										result.address?.street),
								postalCode: a.postalCode ?? result.address?.postalCode,
							};
							result.countryCode = a.countryCode || result.countryCode;
						}
					} catch {}
				}

				const gid =
					order?.group?.id ?? currentGroup?.id ?? currentCategory?.parentId;
				const cid =
					order?.category?.id ?? currentCategory?.id ?? resolvedCategoryId;
				const pid =
					order?.product?.id ?? effectiveProduct?.id ?? resolvedProductId;

				const nextOrder = {
					...(order ?? {}),
					product: effectiveProduct ?? order?.product,
					category: order?.category ?? currentCategory,
					group: order?.group ?? currentGroup,
					location: result as Geo,
				};

				setOrder(nextOrder);

				setIsMapSelection(false);
				setMapExactClickPosition(null);

				if (gid && cid && pid) {
					navigate(
						`/store/groups/${gid}/categories/${cid}/products/${pid}/quantity`,
					);
				} else {
					navigate('/store/groups');
				}
			})();
		},
		[
			order,
			currentGroup,
			currentCategory,
			resolvedCategoryId,
			resolvedProductId,
			effectiveProduct,
			setOrder,
			navigate,
			isMapSelection,
			mapExactClickPosition,
			user?.profile?.countryCode,
		],
	);

	const handleRecentAddressSelect = useCallback(
		({
			address,
			countryCode,
			lat,
			lng,
		}: {
			address: string;
			countryCode: string;
			lat: number;
			lng: number;
		}) => {
			const geo = {
				locationId: '',
				latitude: lat,
				longitude: lng,
				address: {
					label: address,
					countryCode,
				},
				countryCode,
				label: '',
				matchLevel: '',
			} as Geo & { fromRecentOrder?: boolean };

			geo.fromRecentOrder = true;

			setSearch(address);
			setIsMapSelection(false);
			setMapExactClickPosition(null);
			setLocations([]);
			setHasSearched(false);
			handlePreviewLocation(geo).then(() => {});
		},
		[handlePreviewLocation],
	);

	const doNext = (loc?: Geo | null) => {
		const gid =
			order?.group?.id ?? currentGroup?.id ?? currentCategory?.parentId;
		const cid =
			order?.category?.id ?? currentCategory?.id ?? resolvedCategoryId;
		const pid = order?.product?.id ?? effectiveProduct?.id ?? resolvedProductId;

		const nextOrder = {
			...(order ?? {}),
			product: effectiveProduct ?? order?.product,
			category: order?.category ?? currentCategory,
			group: order?.group ?? currentGroup,
			...(loc || previewLocation
				? { location: (loc ?? previewLocation)! }
				: {}),
		};

		setOrder(nextOrder);

		if (gid && cid && pid) {
			navigate(
				`/store/groups/${gid}/categories/${cid}/products/${pid}/quantity`,
			);
		} else {
			navigate('/store/groups');
		}
	};

	const selectedLocationSafe = useMemo(() => {
		const loc = previewLocation || order?.location || null;
		if (!loc) return undefined;
		const lat = toNum(loc.latitude);
		const lng = toNum(loc.longitude);
		if (lat == null || lng == null) return undefined;
		return { ...loc, latitude: lat, longitude: lng };
	}, [previewLocation, order?.location]);

	const mapVariants = {
		normal: {
			width: 662,
			height: 738,
			maxWidth: 'none',
			maxHeight: 'none',
			position: 'static' as const,
		},
		fullscreen: {
			width: '100%',
			height: '100%',
			maxWidth: 1220,
			maxHeight: 1040,
		},
	};

	useEffect(() => {
		const mql = window.matchMedia('(min-width: 1024px)');
		const update = () => setIsLgUp(mql.matches);
		update();
		// Safari support
		if (mql.addEventListener) mql.addEventListener('change', update);
		else mql.addListener(update);
		return () => {
			if (mql.removeEventListener) mql.removeEventListener('change', update);
			else mql.removeListener(update);
		};
	}, []);

	return (
		<div className="w-full flex flex-col gap-5 h-[calc(100vh-60px)] gap-x-2.5 overflow-x-hidden xl:overflow-x-auto mt-[68px] sm:mt-0">
			<div className="w-[calc(100vw-26px)] sm:w-[1102px] xl:self-center flex flex-col gap-4 pt-3.5 sm:pt-0 xl:ml-0 box-border">
				<ClientStoreBreadcrumbs
					currentCategory={order?.category ?? currentCategory}
					currentGroup={order?.group ?? currentGroup}
					currentProduct={effectiveProduct}
					categoryId={order?.category?.id ?? currentCategory?.id}
					storeOnly
					showDeliveryAddress={false}
					contract={order?.contractData}
				/>
			</div>
			<div className="border-0 border-solid border-t-[1px] border-gray-10" />

			{flow === CUSTOMER_STORE_FLOW.CONTRACT_LOCATION_FIRST && (
				<div className="w-[calc(100vw-26px)] sm:w-[1102px] xl:self-center xl:ml-0 box-border">
					<ClientStepper currentStep={1} flow="CONTRACT_LOCATION_FIRST" />
				</div>
			)}
			<div className="border-0 border-solid border-t-[1px] border-gray-10" />

			<div className="sm:w-[calc(100vw-154px)] xl:w-[1102px] xl:self-center flex flex-col gap-5 mb-6 sm:mb-0 sm:ml-12 sm:mr-7.5 xl:mx-0 box-border">
				<div className="flex items-center gap-[40px]">
					<div className="flex items-center justify-center gap-1">
						<div className="w-[92px] h-[80px] flex flex-col items-center justify-center border border-solid border-gray-10 rounded-10 box-border">
							<SypacText variant="body-regular-medium">
								<p className="text-[12px] leading-[14px] text-gray-60">
									<T keyName="clientSelectQuantity.placed">Placed</T>
								</p>
							</SypacText>
							<SypacText variant="body-regular-medium">
								<p className="text-[20px] leading-[32px] text-gray-80">
									{order?.contractData?.orders?.length ?? 0}
								</p>
							</SypacText>
						</div>
						<div className="w-[92px] h-[80px] flex flex-col items-center justify-center border border-solid border-gray-10 rounded-10 box-border">
							<SypacText variant="body-regular-medium">
								<p className="text-[12px] leading-[14px] text-gray-60">
									<T keyName="clientSelectQuantity.inProgress">In progress</T>
								</p>
							</SypacText>
							<SypacText variant="body-regular-medium">
								<p className="text-[20px] leading-[32px] text-gray-80">
									{order?.contractData?.ordersInProgress ?? 0}
								</p>
							</SypacText>
						</div>
						<div className="w-[92px] h-[80px] flex flex-col items-center justify-center border border-solid border-gray-10 rounded-10 box-border">
							<SypacText variant="body-regular-medium">
								<p className="text-[12px] leading-[14px] text-gray-60">
									<T keyName="clientSelectQuantity.delivered">Delivered</T>
								</p>
							</SypacText>
							<SypacText variant="body-regular-medium">
								<p className="text-[20px] leading-[32px] text-gray-80">
									{order?.contractData?.ordersDelivered ?? 0}
								</p>
							</SypacText>
						</div>
					</div>

					{order?.contractData?.lines?.[0].product?.productBase ? (
						<div className="flex flex-col gap-2">
							<SypacText variant="body-regular-medium">
								<p className="text-[14px] leading-[16px] text-gray-90">
									{order?.contractData?.lines?.[0].product?.productBase?.name}
								</p>
							</SypacText>

							<SypacText variant="body-regular-medium">
								<p className="text-[14px] leading-[16px] text-gray-80">
									{
										order?.contractData?.lines?.[0].product?.productBase
											?.address
									}
								</p>
							</SypacText>
						</div>
					) : null}

					{order?.contractData && (
						<div className="w-full flex flex-col justify-between gap-2">
							<LayeredProgressBar
								type={{
									key: 'clientTotalContractQuantity',
									label: 'Total contract quantity',
								}}
								totalTons={selectedContractLine?.contractQtyTons ?? 0}
								segments={[
									{
										value: orderedQtyTons,
										colorClass: 'bg-mountain-meadow',
										tooltip: `${t(
											'layeredProgressBar.ordered',
											'Ordered:',
										)} ${orderedQtyTons} t`,
										enableBrightnessHover: true,
									},
									{
										value: deliveredQtyTons,
										colorClass: 'bg-texas-rose',
										tooltip: `${t(
											'layeredProgressBar.delivered',
											'Delivered:',
										)} ${deliveredQtyTons} t`,
										enableBrightnessHover: true,
									},
								]}
								allowedOverdraftTons={allowedOverdraftTons}
								valueSuffix=" t"
								showSummary
							/>

							{selectedContractLine?.ignoreCreditLimit && (
								<div className="flex items-center gap-2">
									<BankTransfer color="#454545" width={20} height={20} />

									<div className="flex flex-col gap-[2px]">
										<SypacText variant="body-regular-medium">
											<p className="text-[14px] leading-[16px] text-gray-90">
												<T keyName="generalTab.creditLimitDisabled">
													Credit limit disabled
												</T>
											</p>
										</SypacText>
										<SypacText variant="body-regular-medium">
											<p className="text-[14px] leading-[16px] text-gray-50">
												<T keyName="generalTab.creditLimitTooltipBuyer">
													Orders you place from this contract
												</T>
											</p>
										</SypacText>
									</div>
								</div>
							)}
						</div>
					)}
				</div>
			</div>

			<div className="sm:w-[calc(100vw-154px)] xl:w-[1102px] xl:self-center flex flex-col sm:flex-row gap-6 sm:justify-between mb-6 sm:mb-0 mt-2 xl-2xl:mt-3.5 sm:ml-12 sm:mr-7.5 xl:mx-0 box-border">
				<div className="w-[calc(100vw-26px)] sm:w-full sm:max-w-[520px] flex-grow flex flex-col gap-5 ml-3.5 sm:ml-0">
					<section className="flex flex-col gap-4 xl:gap-6 px-3 lg:p-0">
						<SypacText variant="body-regular-large">
							<p className="text-base xl:text-xl text-gray-80">
								<T keyName="clientSelectLocation.whereToDeliverYourGoods">
									Where to deliver your goods?
								</T>
							</p>
						</SypacText>

						<SearchLocation
							inputName="search_location"
							locations={locations}
							locationsLoading={locationsLoading}
							hasSearched={hasSearched}
							search={search}
							selectedLocation={selectedLocationSafe ?? null}
							onChange={(value: React.SetStateAction<string>) => {
								setSearch(value);
								setIsMapSelection(false);
								setMapExactClickPosition(null);
							}}
							selectLocation={handleLocationSelect}
							onPreviewLocation={handlePreviewLocation}
							placeholder={{
								key: 'searchLocation.enterDeliveryPlace',
								label: 'Enter delivery place',
							}}
						/>
					</section>

					{order?.contractData?.orders?.length ? (
						<DeliveryAddresses
							orders={order?.contractData?.orders}
							forStore
							onAddressSelect={handleRecentAddressSelect}
						/>
					) : null}

					<nav className="hidden sm:flex gap-5 justify-center mt-auto">
						<SypacButton variant="secondary" size="small" className="w-full">
							<button
								type="button"
								className="flex flex-row gap-2 px-6 py-[13px] rounded-lg w-full transition"
								onClick={() => {
									navigate(-1);
								}}
							>
								<SypacIcon
									size="custom"
									width="10px"
									height="12px"
									icon-name="Alt Arrow Left"
								/>
								<SypacText variant="body-normal-medium">
									<p className="mt-[3px]">
										<T keyName="clientSelectQuantity.back">Back</T>
									</p>
								</SypacText>
							</button>
						</SypacButton>
						<SypacButton variant="secondary" size="small" className="w-full">
							<button
								type="button"
								className="flex flex-row gap-2 px-6 py-[13px] rounded-lg w-full transition"
								onClick={() => doNext()}
							>
								<SypacText variant="body-normal-medium">
									<p className="mt-[3px]">
										<T keyName="clientSelectQuantity.next">Next</T>
									</p>
								</SypacText>
								<SypacIcon
									size="custom"
									width="10px"
									height="12px"
									icon-name="Alt Arrow Right"
								/>
							</button>
						</SypacButton>
					</nav>
				</div>

				<motion.section
					variants={mapVariants}
					initial="normal"
					animate={isMapFullScreen ? 'fullscreen' : 'normal'}
					transition={{ type: 'spring', stiffness: 200, damping: 30 }}
					className={cn(
						'w-[562px] h-[738px] overflow-hidden box-border',
						isMapFullScreen ? '' : 'hidden lg:flex',
					)}
				>
					{(isMapFullScreen || isLgUp) && (
						<Map
							polygons={[]}
							markers={markers}
							target="customer"
							isMapFullScreen={isMapFullScreen}
							setIsMapFullScreen={setIsMapFullScreen}
							selectedLocation={selectedLocationSafe}
							onConfirmLocation={handleLocationSelect}
							onReversePin={(geo: any) => {
								setIsMapSelection(true);

								const addressLabel = geo.address?.label || '';
								setSearch(addressLabel);

								const lat = toNum(geo.latitude);
								const lng = toNum(geo.longitude);

								const reverseGeoLocation: Geo = {
									locationId: geo.locationId,
									latitude: lat ?? undefined,
									longitude: lng ?? undefined,
									address: geo.address,
									countryCode: geo.countryCode,
									label: '',
									matchLevel: '',
								};

								setPreviewLocation(
									lat != null && lng != null ? reverseGeoLocation : null,
								);
								setLocations([]);
								setHasSearched(false);

								setMapExactClickPosition(geo.exactClickPosition ?? null);
							}}
						/>
					)}
				</motion.section>

				<div className="w-[calc(100vw-26px)] flex flex-col sm:hidden gap-5 justify-center ml-3.5">
					<SypacButton className="w-full" variant="secondary" size="small">
						<button
							type="button"
							className="flex flex-row gap-2 px-6 py-[13px] rounded-lg w-full transition"
							onClick={() => doNext()}
						>
							<SypacText variant="body-normal-medium">
								<p className="mt-[3px]">
									<T keyName="clientSelectQuantity.next">Next</T>
								</p>
							</SypacText>
							<SypacIcon
								size="custom"
								width="10px"
								height="12px"
								icon-name="Alt Arrow Right"
							/>
						</button>
					</SypacButton>
					<SypacButton variant="secondary" size="small" className="w-full">
						<button
							type="button"
							className="flex flex-row gap-2 px-6 py-[13px] rounded-lg w-full transition"
							onClick={() => {
								navigate(-1);
							}}
						>
							<SypacIcon
								size="custom"
								width="10px"
								height="12px"
								icon-name="Alt Arrow Left"
							/>
							<SypacText variant="body-normal-medium">
								<p className="mt-[3px]">
									<T keyName="clientSelectQuantity.back">Back</T>
								</p>
							</SypacText>
						</button>
					</SypacButton>
				</div>
			</div>
		</div>
	);
};

export default SelectLocationStep;
